import { useEffect, useMemo, useState } from "react";
import { Box, Pagination, Typography } from "@mui/material";

type ClientSidePaginationProps = {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  "aria-label": string;
};

export function ClientSidePagination({
  page,
  pageSize,
  total,
  onPageChange,
  "aria-label": ariaLabel,
}: ClientSidePaginationProps) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  if (total <= pageSize) return null;

  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1,
        mt: 2,
      }}
    >
      <Typography variant="caption" color="text.secondary" component="p">
        Prikaz {start} - {end} od {total}
      </Typography>
      <Pagination
        count={pageCount}
        page={page}
        onChange={(_, next) => onPageChange(next)}
        color="primary"
        size="small"
        showFirstButton
        showLastButton
        aria-label={ariaLabel}
      />
    </Box>
  );
}

export function sliceForPage<T>(
  items: T[],
  page: number,
  pageSize: number,
): T[] {
  const p = Math.max(1, page);
  const start = (p - 1) * pageSize;
  return items.slice(start, start + pageSize);
}

export function useClientSidePagination<T>(
  items: T[],
  pageSize: number,
  resetKey?: string | number,
) {
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [resetKey]);

  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const effectivePage = Math.min(Math.max(1, page), totalPages);

  const slice = useMemo(
    () => sliceForPage(items, effectivePage, pageSize),
    [items, effectivePage, pageSize],
  );

  return {
    page: effectivePage,
    setPage,
    slice,
    firstIndex: (effectivePage - 1) * pageSize,
    total: items.length,
    pageSize,
  };
}
